import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import MenuItemArrow from "./MenuItemArrow";

interface MenuItemProps {
  item: any;
  isScrolled: boolean;
  isHover: boolean;
  activeMain: any;
  isTouch: boolean;
  onToggleMainMenu: (id: any, isWithOutSubMenu?: boolean) => void;
  onHoverMainMenu: (id: any) => void;
  onHoverEnd: () => void;
  onSetActiveMenu: (label: string) => void;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isScrolled,
  isHover,
  activeMain,
  isTouch,
  onToggleMainMenu,
  onHoverMainMenu,
  onHoverEnd,
  onSetActiveMenu,
}) => {
  const router = useRouter();
  // Set after a desktop click navigates away. The pointer is still resting on
  // the title, so hover styling and hover-to-open stay off until it leaves.
  const [hoverLocked, setHoverLocked] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (isTouch) {
      // On touch (iPad/tab/mobile)

      // If item has submenu and it is NOT currently open
      if (item.submenu?.length > 0 && activeMain !== item.id) {
        e.preventDefault();
        onSetActiveMenu(item.lable);
        onToggleMainMenu(item.id);
        return; // Stop here, do not navigate yet
      }

      // If we reach here, either:
      // a) Item has no submenu (handled mostly by handleNoSubMenuClick, but safety check here)
      // b) Submenu is ALREADY open (This is the second tap -> Navigate)

      if (item.link && item.link !== "#!") {
        router.push(item.link);
      } else {
        // If no link (e.g. wrapper only), just toggle (likely close)
        onToggleMainMenu(item.id);
      }
    } else {
      // Desktop (normal click)
      if (item.link && item.link !== "#!") {
        onSetActiveMenu(item.lable);
        onToggleMainMenu(null);
        setHoverLocked(true);
        router.push(item.link);
      } else {
        if (activeMain !== item.id) {
          onToggleMainMenu(item.id);
        }
      }
    }
  };

  // The parent decides whether to open now or after a short hover-intent
  // delay; it is also told about the active title so it can cancel a pending
  // switch when the pointer comes back.
  const handleMouseOver = () => {
    if (!isTouch && !hoverLocked) {
      onHoverMainMenu(item.id);
    }
  };

  const handleNoSubMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onToggleMainMenu(item.id, true);
    router.push(item.link);
  };

  return (
    <div key={item.id} className="relative lg:last:mr-[32px]">
      {/*  md:last:mr-[32px] */}
      <Link
        href={item?.link || "#!"}
        className={`
          ${hoverLocked ? "" : "hover:text-blue-500"} xl:text-subtitle-4 whitespace-nowrap lg:text-subtitle-5 font-semibold
          group-hover/header:text-[#040405] text-white
          ${
            isScrolled
              ? `text-[#040405] ${hoverLocked ? "" : "hover"}`
              : isHover
                ? `text-[#040405] ${hoverLocked ? "" : "hover"}`
                : "text-[#fff] no-hover"
          }
          cursor-pointer
          ${activeMain === item.id ? "text-blue-500" : ""}
          no-active-menu
          menu-box lg:border-0 border-b border-[var(--color-blue-100)]
        `}
        onClick={
          isTouch && item.submenu?.length === 0
            ? handleNoSubMenuClick
            : handleClick
        }
        onMouseOver={
          isTouch && item.submenu?.length === 0 ? undefined : handleMouseOver
        }
        onMouseLeave={() => {
          setHoverLocked(false);
          onHoverEnd();
        }}
      >
        {item?.lable || ""}

        <MenuItemArrow isActive={activeMain === item.id} />
      </Link>
    </div>
  );
};

export default MenuItem;
